import React, { useState } from 'react'
import LabTagSkeleton from '../components/Lab-Group-Details/LabTagSkeleton'
import { Link, NavLink, Outlet, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getSubsModules, getUserAccessInfo } from '../lib/api/Subscription-Service'

import { SearchIcon } from 'lucide-react'
import { ArrowBack } from '../lib/assets/icons/SVG-Icons'

const SubscriptionModuleLayout = () => {
  const { planId, moduleId } = useParams()
  const [searchQuery, setSearchQuery] = useState('')

  const {
    data: moduleData,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: ['get-subscription-group-modules', planId],
    queryFn: () => getSubsModules(planId),
    enabled: !!planId,
  })

  const isLoggedIn = localStorage.getItem('access_token')

  const { data: accessInfo } = useQuery({
    queryKey: ['get-user-access-info', moduleId],
    queryFn: () => getUserAccessInfo(),
    enabled: !!moduleId && !!isLoggedIn,
  })

  function checkAccessByPlanId(planId) {
    if (!isLoggedIn) return false
    const accessList = accessInfo?.data?.accessInfo || []
    const plan = accessList.find((item) => item.planId === planId)
    return plan ? plan.hasAccess : false
  }

  function handleNavLink(moduleId) {
    return `/subscription/${moduleData?.slug}/${planId}/${moduleId}`
  }

  function handleBackLink() {
    if (!moduleId) return `/subscription`
    return `/subscription/${moduleData?.slug}/${planId}`
  }

  // Dynamic section title based on moduleId and moduleData
  const sectionTitle = (() => {
    if (!moduleData) return moduleId ? 'Labs' : 'Modules'

    if (!moduleId) {
      return moduleData.title || 'Modules'
    }

    const currentModule = moduleData.modules?.find((module) => module._id === moduleId)
    return currentModule?.title || 'Labs'
  })()

  return (
    <div className="space-y-4">
      <div className="w-full flex flex-col lg:flex-row justify-between lg:items-center gap-4">
        <Link to={handleBackLink()} className="flex items-center gap-2 text-xl font-semibold">
          <figure className="size-6 flex items-center justify-center bg-primary/10 rounded-full shadow-sm">
            <ArrowBack className="stroke-primary w-4 h-4" />
          </figure>
          {sectionTitle}
        </Link>

        <div className="relative w-full max-w-none lg:max-w-xl">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon className="w-4 h-4 text-gray-800" />
          </span>
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue text-gray-900 placeholder-gray-400"
          />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-6">
        <Outlet context={{ searchQuery, checkAccessByPlanId }} />
        <div
          className={`flex flex-col gap-4 duration-300 ease-in-out bg-white h-fit py-3 rounded-md w-full lg:w-72 px-3 ${
            !moduleId && 'hidden'
          }`}>
          {isLoading ? (
            <div className="w-full flex flex-col justify-center flex-wrap">
              <LabTagSkeleton isFirst={true} />
              <LabTagSkeleton />
              <LabTagSkeleton />
              <LabTagSkeleton />
              <LabTagSkeleton />
            </div>
          ) : (
            isSuccess && (
              <div className="flex flex-col">
                {moduleData.modules.map((moduleInfo, i) => (
                  <NavLink
                    key={i}
                    to={handleNavLink(moduleInfo._id)}
                    className={({ isActive }) =>
                      `relative text-left py-2.5 px-4 transition-all border-l border-blue  duration-300 ${
                        isActive
                          ? 'bg-blue/10 text-blue font-semibold border-l-3 border-blue -ml-[1px]'
                          : 'text-[#1E2144] hover:bg-blue/10 hover:text-blue'
                      }`
                    }>
                    {moduleInfo.title}
                  </NavLink>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionModuleLayout
